import React, { PureComponent } from "react"
import Paper from "@mui/material/Paper"
import withStyles from "@mui/styles/withStyles"
import { connect } from "react-redux"
import { Field, FieldArray, reduxForm } from "redux-form"
import MuiCheckbox from "@mui/material/Checkbox"
import FormControl from "@mui/material/FormControl"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Accordion from "@mui/material/Accordion"
import AccordionSummary from "@mui/material/AccordionSummary"
import AccordionDetails from "@mui/material/AccordionDetails"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import Button from "@mui/material/Button"
import Divider from "@mui/material/Divider"

import { reportRulesInitialValues } from "../../../../actions/Utils/utils"
import { COLORS, ACTIVE_KFC_BRANDS } from "../../../../utils"
import ReduxTextField from "../../../../components/form/ReduxTextField"
import Checkbox from "../../../../components/CheckBoxField"
import RadioField from "../../../../components/RadioField"
import { updateReportRules } from "../../../../actions/Planning/Settings/PlanningReport"

const styles = {
    root: {
        width: "100%",
        height: "100%",
        position: "relative",
        display: "flex"
    },
    formContainer: {
        width: "100%",
    },
    formFieldsContainer: {
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gridGap: 10,
        margin: 25,
        paddingBottom: 72,
    },
    buttonContainer: {
        composes: "flexRow",
        justifyContent: "flex-end",
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: COLORS.WHITE,
        borderTop: `1px solid ${COLORS.MEDIUM_GREY}`,
        zIndex: 10,
    },
    brandTitle: {
        lineHeight: "32px",
        color: COLORS.WHITE,
        paddingLeft: 16,
        borderRadius: "4px 4px 0px 0px"
    },
    brandFormContainer: {
        border: `1px solid ${COLORS.MEDIUM_GREY}`,
        width: "100%",
        borderRadius: 4
    },
    subPartContainer: {
        margin: "10px 32px",
        display: "flex",
        flexDirection: "column"
    },
    subPartTitle: {
        textDecoration: "underline"
    },
    field: {
        marginTop: 0,
        marginBottom: 0,
    },
    subFormContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
    },
    subFormLabel: {
        justifyContent: "center",
        verticalAlign: "middle",
        marginTop: 16
    },
    actionButton: {
        margin: 16,
    },
}

// FieldArray renderer — one row per possible target, checkbox controls active state
const TargetsList = ({ fields, allTargets, entityKey, reportRulesFormValues }) => {
    const currentVolumeReporting = reportRulesFormValues?.[entityKey]?.volumeReporting
    const currentTargets = reportRulesFormValues?.[entityKey]?.autoSaveableTargets || []

    const handleToggle = (target, isActive, activeIdx) => {
        if (isActive) {
            fields.remove(activeIdx)
        } else {
            fields.push({ targetKey: target.key, saleDate: "" })
        }
    }

    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell></TableCell>
                        <TableCell align="right">Décalage en jours antérieurs</TableCell>
                        <TableCell align="right">Planning Vente</TableCell>
                        <TableCell align="right">Volume</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {allTargets.map(target => {
                        const activeIdx = currentTargets.findIndex(t => t.targetKey === target.key)
                        const isActive = activeIdx >= 0

                        return (
                            <TableRow key={target.key}>
                                <TableCell>{target.label}</TableCell>
                                <TableCell align="right">
                                    {isActive && (
                                        <Field
                                            name={`${entityKey}.autoSaveableTargets[${activeIdx}].saleDate`}
                                            type="number"
                                            component={ReduxTextField}
                                            style={{ width: 80 }}
                                        />
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    <MuiCheckbox
                                        checked={isActive}
                                        onChange={() => handleToggle(target, isActive, activeIdx)}
                                        color="secondary"
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <Field
                                        name={`${entityKey}.volumeReporting`}
                                        component={RadioField}
                                        props={{ value: target.key, selectedValue: currentVolumeReporting }}
                                    />
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

const buildLiveSummaryItems = (formValues, allEntities) => {
    if (!formValues) return []
    const targets = (formValues.autoSaveableTargets || [])
        .filter(t => t?.targetKey)
        .map(t => {
            const found = allEntities.find(e => e.key === t.targetKey)
            return { label: `${found?.label || t.targetKey}${t.saleDate ? ` (${t.saleDate}j)` : ""}`, type: "target" }
        })
    const volume = formValues.volumeReporting
        ? [{ label: `Vol. ${allEntities.find(e => e.key === formValues.volumeReporting)?.label || formValues.volumeReporting}`, type: "volume" }]
        : []
    return [...targets, ...volume]
}

export class PlanningSettingReportForm extends PureComponent {

    render() {
        const { classes, handleSubmit, updateReportRules, reportRules, reportRulesFormValues } = this.props

        const allEntities = [
            ...ACTIVE_KFC_BRANDS.map(b => ({ key: b.name, label: b.label, color: b.color })),
            ...Object.values(reportRules || {})
                .filter(r => r?._isEnterprise)
                .map(r => ({ key: r._enterpriseId, label: r._enterpriseName, color: r._enterpriseColor || COLORS.BLUEGREY_700 }))
        ]

        return (
            <Paper className={classes.root} elevation={0}>
                <form onSubmit={handleSubmit(updateReportRules)} className={classes.formContainer}>
                    <div className={classes.formFieldsContainer}>
                        {allEntities.map(entity => {
                            const allTargets = allEntities.filter(e => e.key !== entity.key)
                            const summaryItems = buildLiveSummaryItems(reportRulesFormValues?.[entity.key], allEntities)

                            return (
                                <div className={classes.brandFormContainer} key={entity.key}>
                                    <div className={classes.brandTitle} style={{ backgroundColor: entity.color }}>
                                        {entity.label}
                                    </div>
                                    <Accordion
                                        elevation={0}
                                        disableGutters
                                        defaultExpanded={false}
                                        sx={{ "&::before": { display: "none" }, backgroundColor: "transparent" }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            sx={{ px: "32px", minHeight: "36px !important", "& .MuiAccordionSummary-content": { margin: "8px 0", alignItems: "center" } }}
                                        >
                                            <span className={classes.subPartTitle} style={{ marginRight: 8, flexShrink: 0 }}>Se reporte</span>
                                            {summaryItems.length > 0 && (
                                                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                                                    {summaryItems.map((item, i) => (
                                                        <span key={i} style={{ fontSize: "0.75em", padding: "1px 6px", borderRadius: 3, background: item.type === "volume" ? "#fff3e0" : "#e3f2fd", color: "#444", whiteSpace: "nowrap" }}>
                                                            {item.label}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ padding: "8px 32px 16px" }}>
                                            <FieldArray
                                                name={`${entity.key}.autoSaveableTargets`}
                                                component={TargetsList}
                                                props={{ allTargets, entityKey: entity.key, reportRulesFormValues }}
                                            />
                                        </AccordionDetails>
                                    </Accordion>
                                    <Divider />
                                    <div className={classes.subPartContainer}>
                                        <span className={classes.subPartTitle}>Interne</span>
                                        <div className={classes.subFormContainer}>
                                            <span className={classes.subFormLabel}>Production: Jours avant la vente:</span>
                                            <FormControl margin="normal" className={classes.field} required>
                                                <Field name={`${entity.key}.RECIPE.productionDate`} type="number" component={ReduxTextField} />
                                            </FormControl>
                                        </div>
                                        <div className={classes.subFormContainer}>
                                            <span className={classes.subFormLabel}>Barquettage: Jours après la prod:</span>
                                            <FormControl margin="normal" className={classes.field} required>
                                                <Field name={`${entity.key}.RECIPE.packagingDate`} type="number" component={ReduxTextField} />
                                            </FormControl>
                                        </div>
                                    </div>
                                    <Divider />
                                    <div className={classes.subPartContainer}>
                                        <span className={classes.subPartTitle}>Externe</span>
                                        <div className={classes.subFormContainer}>
                                            <span className={classes.subFormLabel}>Production: Jours avant la vente:</span>
                                            <FormControl margin="normal" className={classes.field} required>
                                                <Field name={`${entity.key}.SUBCONTRACTORPRODUCT.productionDate`} type="number" component={ReduxTextField} />
                                            </FormControl>
                                        </div>
                                        <div className={classes.subFormContainer}>
                                            <span className={classes.subFormLabel}>Barquettage: Jours après la prod:</span>
                                            <FormControl margin="normal" className={classes.field} required>
                                                <Field name={`${entity.key}.SUBCONTRACTORPRODUCT.packagingDate`} type="number" component={ReduxTextField} />
                                            </FormControl>
                                        </div>
                                    </div>
                                    <Divider />
                                    <div className={classes.subPartContainer}>
                                        <div className={classes.subFormContainer}>
                                            <span className={classes.subFormLabel}>Volumes de barquettage incompressibles:</span>
                                            <FormControl margin="normal" className={classes.field}>
                                                <Field name={`${entity.key}.incompressiblePackagingVolumes`} type="checkbox" component={Checkbox} />
                                            </FormControl>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                    <div className={classes.buttonContainer}>
                        <Button className={classes.actionButton} variant="contained" color="primary" type="submit">
                            Enregistrer
                        </Button>
                    </div>
                </form>
            </Paper>
        )
    }
}

const validate = (values) => {
    const errors = {}
    Object.keys(values || {}).forEach(entityKey => {
        const targets = (values[entityKey] && values[entityKey].autoSaveableTargets) || []
        const targetsErrors = targets.map(t => (t && (t.saleDate === "" || t.saleDate == null) ? { saleDate: "Requis" } : undefined))
        if (targetsErrors.some(Boolean)) {
            errors[entityKey] = { autoSaveableTargets: targetsErrors }
        }
    })
    return errors
}

const ReportForm = reduxForm({
    form: "reportRules",
    enableReinitialize: true,
    validate,
})(PlanningSettingReportForm)

const PlanningSettingReportFormWithStyles = withStyles(styles)(ReportForm)

let _cachedReportRules = null
let _cachedInitialValues = null
const selectInitialValues = (reportRules) => {
    if (reportRules !== _cachedReportRules) {
        _cachedReportRules = reportRules
        _cachedInitialValues = reportRulesInitialValues(reportRules)
    }
    return _cachedInitialValues
}

export default connect((state) => {
    return {
        reportRulesFormValues: state.form?.reportRules?.values,
        initialValues: selectInitialValues(state.planningSettings.reportRules),
        reportRules: state.planningSettings.reportRules,
    }
}, {
    updateReportRules
})(PlanningSettingReportFormWithStyles)
